import { Button, pxToRem, useStyles, useTheme } from '@archipro-design/aria';
import { ArrowLeftSLine } from '@archipro-design/icons';
import * as S from './DetailBannerSection.style';
import type {
    ColumnDimensionType,
    TopBanner,
} from '~/modules/page-builder/types';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import { useClickBack } from '~/modules/root/hook/use-click-back';
import React from 'react';
import ImageCaption from '~/modules/page-builder/component/common/image-caption/ImageCaption';
import { ImageCaptionContainer } from '@modules/page-builder/component/detail/detail-single-image-shell/DetailSingleImageShell.style';
import type { AwardProps } from '@modules/page-builder/component/detail/banner-awards/types';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { hasFeature, useAppData } from '@modules/root';

interface DetailBannerSectionProps extends TopBanner {
    dimensions: ColumnDimensionType;
    size: string;
    title?: string;
    actions?: React.ReactNode[];
    showBackButton?: boolean;
    showActionsOnHover?: boolean;
    caption?: string;
    awards?: AwardProps[];
    customOverlayElement?: React.ReactNode;
    showOverlay?: boolean;
}

const DetailBannerSection = ({
    dimensions,
    imageUrl,
    introText,
    size,
    title,
    actions = [],
    showBackButton,
    showActionsOnHover = false,
    caption,
    awards,
    customOverlayElement,
    showOverlay = false,
}: DetailBannerSectionProps) => {
    const { css } = useStyles({
        dimension: dimensions,
        showActionsOnHover,
        showOverlay,
    });
    const theme = useTheme();
    const { user } = useAppData();
    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    const { onClick: onClickBack } = useClickBack();

    const altText =
        caption ?? introText ?? (title ? `${title} banner` : 'banner');

    return (
        <div className={css(S.BannerWrapper)} id={'top-banner'}>
            <div className={css(S.ImageContainer)}>
                {imageUrl && (
                    <>
                        {
                            <ImageWithPaintPicker
                                key={imageUrl}
                                src={imageUrl}
                                layout={'fill'}
                                placeholderColor="light"
                                width={dimensions.width}
                                height={dimensions.height}
                                loading={'eager'}
                                sizes={size}
                                objectFit={'cover'}
                                alt={altText}
                            />
                        }
                    </>
                )}
                {showBackButton && (
                    <>
                        <Button
                            as="a"
                            transparent={true}
                            icon={
                                <ArrowLeftSLine
                                    variables={{
                                        color: theme.siteVariables.colors
                                            .white[100],
                                    }}
                                />
                            }
                            variables={{
                                buttonPadding: pxToRem(12),
                                iconPadding: 0,
                            }}
                            size={30}
                            className={css(S.BackButton)}
                            onClick={onClickBack}
                        />
                    </>
                )}
                {enableAwardBadges && awards && awards.length > 0 && (
                    <BannerAwards awards={awards} />
                )}
                <div role="toolbar" className={css(S.HoverActions)}>
                    {...actions}
                </div>
            </div>
            {caption && (
                <div className={css(S.MobileCaptionContainer)}>
                    {caption && (
                        <div className={css(ImageCaptionContainer)}>
                            <ImageCaption text={caption} />
                        </div>
                    )}
                </div>
            )}
            {customOverlayElement}
        </div>
    );
};

export default DetailBannerSection;
